import { compareAsc, differenceInMonths, isBefore } from "date-fns";
import { AnswerFragment, AnswerSummaryFragment, QuestionTypeEnum, ReportFragment, ReviewFullFragment, ReviewReportFullFragment, UserMinimalFragment } from '../graphql/generated/definitions';

/** Extract from an array of reports every answers that answers a Question of the passed type */
export function extractAnswersType(
  reports: (ReportFragment | ReviewReportFullFragment)[],
  type: QuestionTypeEnum
): AnswerSummaryFragment[] {
  return reports
    .flatMap((report) => {
      return report?.answers.filter((ra) => {
        return ra.question.type === type;
      });
    })
    .filter((ra) => ra !== undefined);
}

/**
 * Get last review report
 * Note: This function only take into account checked reports
 */
export function getLastReport(review: ReviewFullFragment): ReviewReportFullFragment {
  let last = review.reports?.[0];

  review.reports.forEach((report) => {
    if (
      report.checkedAt &&
      isBefore(new Date(last.checkedAt), new Date(report.checkedAt))
    )
      last = report;
  });

  return last;
}

type OverdueReport = {
  report: ReviewReportFullFragment;
  reason: "collaborator_overdue" | "referent_overdue";
  concernedUsers: UserMinimalFragment[];
};

/**
 * Give back overdue reports as well as usef informations to identify where the problem is
 */
export function getOverdueReports(review: ReviewFullFragment): OverdueReport[] {
  const overdues: OverdueReport[] = [];

  const isOverdue = (report) => {
    if (
      !report?.filledAt &&
      (isBefore(new Date(review.nextReportAt), new Date(report?.createdAt)) ||
        differenceInMonths(new Date(), new Date(report?.createdAt)) >= 1)
    )
      overdues.push({
        report,
        reason: "collaborator_overdue",
        concernedUsers: [review.collaborator],
      });
    else if (
      !report?.checkedAt &&
      (isBefore(new Date(review.nextReportAt), new Date(report?.filledAt)) ||
        differenceInMonths(new Date(), new Date(report?.createdAt)) >= 1)
    )
      overdues.push({
        report,
        reason: "referent_overdue",
        concernedUsers: [...report.reportReferents].map((rr) => rr.referent),
      });
  };

  if (review.reports.length === 0) isOverdue(undefined);
  review.reports.forEach((report) => isOverdue(report));

  return overdues;
}

export type ReportQuestionAverage = {
  questionDigest: string;
  answers: AnswerSummaryFragment[];
  averageGrade: number;
  averageGradeLabel?: string;
  averageTimeSpent?: number;
  gradeCount: number;
  timeSpentCount?: number;
};

export function getReportStatus(
  review: ReviewFullFragment,
  report?: ReviewReportFullFragment
) {
  if (isBefore(new Date(), new Date(review.startDate))) return "not_started";

  if (!report?.filledAt && isBefore(new Date(review.startDate), new Date()))
    return "awaiting_collaborator";
  if (
    review.schedule === null &&
    (!review.endDate || report.type === "FinalReport") &&
    report.checkedAt
  )
    return "review_ended";
  if (report?.checkedAt) return "report_ready";
  if (report?.filledAt && review.referents.length > 0) return "awaiting_referents";
}

/**
 * Return the averaged grade of a group of answers as well as other useful informations
 * Note: it will purge every answers that are not either of type 'QuestionSkill' or 'QuestionEvaluation'
 */
export function getReferentGradeAverage(
  answers: AnswerFragment[],
  referentsIds: string[]
) {
  const average: ReportQuestionAverage[] = [];

  answers.forEach((ra) => {
    if (ra.type !== "AnswerSkill" && ra.type !== "AnswerEvaluation") return;

    const current = average.find(
      (a) => a.questionDigest === ra.question.digest
    );

    if (ra.value.selector) {
      if (current && referentsIds.includes(ra.user.id)) {
        current.gradeCount += 1;
        current.averageGrade += parseInt(ra.value.selector as string, 10);
        current.answers.push(ra);
        if (ra.timeSpent) {
          current.timeSpentCount += 1;
          current.averageTimeSpent += ra.timeSpent;
        }
      } else if (referentsIds.includes(ra.user.id)) {
        average.push({
          questionDigest: ra.question.digest,
          averageGrade: parseInt(ra.value.selector as string, 10),
          answers: [ra],
          gradeCount: 1,
          averageTimeSpent: ra.timeSpent,
          timeSpentCount: ra.timeSpent ? 1 : 0,
        });
      }
    }
  });

  average.forEach((e) => {
    e.averageGrade /= e.gradeCount;
    const currentOption = e.answers[0].question?.options.find(
      (o) => parseInt(o.value) === Math.floor(e.averageGrade)
    );
    e.averageGradeLabel = currentOption
      ? currentOption.label
      : "Unknown option";
    e.averageTimeSpent /= e.timeSpentCount;
  });

  return average;
}

export function getCollaboratorUserData(answers: AnswerFragment[]) {
  const collaboratorData: {
    user: UserMinimalFragment;
    reports: {
      id: string;
      date: string;
      answers: AnswerFragment[];
    }[];
  }[] = [];

  // First loop to create every collaborator & reports entries
  answers.forEach((ra) => {
    if (!ra.isCollaborator) return;

    const collaborator = collaboratorData.find(({ user }) => ra.user.id === user.id);
    const isReportDup = !!collaboratorData.find(({ reports }) =>
      reports.find((report) => report.id === ra.reportId)
    );

    if (isReportDup) return;
    if (!collaborator)
      collaboratorData.push({
        user: ra.user,
        reports: [{ id: ra.reportId, date: ra.report.createdAt, answers: [] }],
      });
    else
      collaborator.reports.push({
        id: ra.reportId,
        date: ra.report.createdAt,
        answers: [],
      });
  });

  // Append to each reports all the answers
  answers.forEach((ra) => {
    const collaborator = collaboratorData.find(({ reports }) =>
      reports.find((report) => report.id === ra.reportId)
    );

    if (collaborator) {
      const report = collaborator.reports.find(({ id }) => id === ra.reportId);

      if (report) {
        const dup = report.answers.find(({ id }) => id === ra.id);

        if (!dup) report.answers.push(ra);
      }
    }
  });

  // Ordering them by date ASC
  collaboratorData.forEach(({ reports }) => {
    reports.sort((i, j) => compareAsc(new Date(i.date), new Date(j.date)));
    reports.forEach(({ answers }) =>
      answers.sort((i, j) =>
        compareAsc(new Date(i.updatedAt), new Date(j.updatedAt))
      )
    );
  });

  return collaboratorData;
}

/** Retrieves the closest option to the given grade */
export function getClosestGrade(
  counts: {
    value: number;
    label: string;
  }[],
  goal: number
) {
  return counts.reduce((prev, curr) =>
    Math.abs(curr.value - goal) < Math.abs(prev.value - goal) ? curr : prev
  );
}
